import React, { type ChangeEvent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Icon, InlineMessage, InputField } from '@saviynt/design-system';
import cx from 'classnames';

import { CF_NEU_STL, SEARCH_TEXT_LENGTH } from '../../../shared/constants';
import {
  useDebounceForFunctions,
  useInvertedLocalize,
} from '../../../shared/hooks';
import { violationsTabActions } from '../../ViolationsTab/data-store/violationsTabSlice';

/**
 * ReportPreviewSearch
 */
function ReportPreviewSearch() {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const [isSearchExpaded, setIsSearchExpaded] = useState(false);

  const searchBoxClasses = cx(
    'SodControls-searchField',
    isSearchExpaded
      ? 'SodControls-searchField--isExpanded'
      : 'SodControls-searchField--isCollapsed'
  );

  const searchPlaceholder = 'Search your text here...';
  const [searchText, setSearchText] = useState('');

  // no additional selectors needed; preview is centrally triggered

  const handleSearch = useCallback(
    (searchValue: string) => {
      // Persist search text in Redux; centralized preview effect will run
      dispatch(violationsTabActions.setSearchKey(searchValue || null));
    },
    [dispatch]
  );

  const handleLocalSearch = useCallback(
    (text: string) => {
      if (text.trim().length < SEARCH_TEXT_LENGTH && text.length !== 0) {
        setIsSearchAlert(true);

        return;
      }

      setIsSearchAlert(false);

      handleSearch(text.trim());
    },
    [handleSearch]
  );

  const handleDebouncedSearch = useDebounceForFunctions(
    (e: ChangeEvent<HTMLInputElement>) => handleLocalSearch(e.target.value),
    500
  );

  const handleSearchBoxClose = () => {
    handleSearch('');
    setSearchText('');
    setIsSearchExpaded(false);
    setIsSearchAlert(false);
  };

  const [isSearchAlert, setIsSearchAlert] = useState(false);

  return (
    <Box tag='div' className={searchBoxClasses} tabIndex={0}>
      <InputField
        name='violation-name-search'
        placeholder={searchPlaceholder}
        kind='searchCollapse'
        size='medium'
        ariaLabel={l('Search')}
        prefixIcon={<Icon kind='Search' color={CF_NEU_STL} />}
        value={searchText}
        shouldDisallowBrackets
        setValue={setSearchText}
        onChange={handleDebouncedSearch}
        resetValue={handleSearchBoxClose}
        isSearchExpanded={isSearchExpaded}
        setIsSearchExpanded={setIsSearchExpaded}
      />
      {isSearchAlert && isSearchExpaded && (
        <InlineMessage
          text={l('Type more than 2 characters to see results.')}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' color='critical-700' />}
        />
      )}
    </Box>
  );
}

export default ReportPreviewSearch;
